import { currencyLabel } from '@/helpers/currency';
import { useTranslation } from '@/i18n';
import { Card, Sparkline, Tooltip } from '@payaca/ambrosia';

import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo } from 'react';
import {
  calculateYAxisRange,
  hasSparklineData,
} from '../hooks/useSparklineOptions';
import { CHART_HEIGHT, getDateLabels } from '../utils';

export interface MetricCardProps {
  title: string;
  value: number;
  previousValue?: number;
  percentageChange?: number | null;
  formatValue: (value: number) => string;
  isWarning?: boolean;
  sparkline?: number[];
  color?: string;
  emptyMessage?: string;
  tooltip?: string;
  region?: string;
  comparisonPeriodLabel?: string;
}

const MetricCard: FC<MetricCardProps> = ({
  title,
  value,
  previousValue,
  percentageChange,
  formatValue,
  isWarning,
  sparkline,
  color = '#22c55e', // green-500
  emptyMessage,
  tooltip,
  region,
  comparisonPeriodLabel,
}) => {
  const translate = useTranslation();
  const isPositive = percentageChange != null && percentageChange >= 0;
  const hasChange = percentageChange != null;
  const isEmpty =
    value === 0 && (!sparkline || sparkline.every((v) => v === 0));

  const dayLabels = useMemo(
    () => getDateLabels(sparkline?.length ?? 30, region),
    [sparkline?.length, region]
  );

  const yAxisRange = useMemo(() => calculateYAxisRange(sparkline), [sparkline]);

  const currencyCode = currencyCodeForRegion[region as AccountRegions];

  const tooltipFormatter = useCallback(
    (val: number) => currencyLabel(val * 100, currencyCode),
    [currencyCode]
  );

  const hasSparkline = hasSparklineData(sparkline);

  if (isEmpty && emptyMessage) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50 shadow-none">
        <Card.Body className="flex flex-1 flex-col justify-between">
          <h3 className="flex items-center gap-1.5">
            {title}
            {tooltip && (
              <Tooltip tooltipContent={tooltip} className="translate-y-0.5">
                <UntitledIcon
                  name="info-circle.3"
                  className="size-4 text-gray-400"
                />
              </Tooltip>
            )}
          </h3>
          <div className="flex flex-1 flex-col items-center justify-center py-4 text-center">
            <h4>{formatValue(0)}</h4>
            <p className="mt-2 supporting-body text-gray-500">{emptyMessage}</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body className="space-y-4">
        <h3 className="flex items-center gap-1.5">
          {title}
          {tooltip && (
            <Tooltip tooltipContent={tooltip} className="translate-y-0.5">
              <UntitledIcon
                name="info-circle.3"
                className="size-4 text-gray-400"
              />
            </Tooltip>
          )}
        </h3>

        <div className="grid grid-cols-1 items-center gap-2 @screen-sm:grid-cols-2">
          <h4>{formatValue(value)}</h4>

          {hasSparkline && (
            <div style={{ height: CHART_HEIGHT.METRIC_SPARKLINE }}>
              <Sparkline
                data={sparkline!}
                color={color}
                height={CHART_HEIGHT.METRIC_SPARKLINE}
                name={title}
                xAxisLabels={dayLabels}
                yAxisMin={yAxisRange.min}
                yAxisMax={yAxisRange.max}
                tooltipFormatter={tooltipFormatter}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-x-2">
          {hasChange && (
            <span
              className={`inline-flex items-center gap-x-1 rounded-full px-1.5 py-1 supporting-body ${
                isPositive
                  ? 'bg-cyan-100 text-cyan-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              <UntitledIcon
                name={isPositive ? 'arrow-up' : 'arrow-down'}
                className="size-3 shrink-0"
              />
              {Math.abs(percentageChange).toFixed(1)}%
            </span>
          )}
          {hasChange && previousValue != null && (
            <p className="supporting-body text-gray-500">
              {isPositive
                ? translate('dashboard.metric.up')
                : translate('dashboard.metric.down')}{' '}
              {translate('dashboard.metric.from')} {formatValue(previousValue)}
              {comparisonPeriodLabel && ` ${comparisonPeriodLabel}`}
            </p>
          )}
          {isWarning && value > 0 && (
            <span className="inline-flex items-center gap-x-1 rounded-full bg-yellow-100 px-1.5 py-1 supporting-body text-yellow-800">
              <UntitledIcon
                name="alert-triangle.3"
                className="size-3 shrink-0"
              />
              {translate('dashboard.metric.overdue')}
            </span>
          )}
          {isWarning && value === 0 && (
            <span className="inline-flex items-center gap-x-1 rounded-full bg-cyan-100 px-1.5 py-1 supporting-body text-cyan-800">
              <UntitledIcon name="check-circle.3" className="size-3 shrink-0" />
              {translate('dashboard.metric.allClear')}
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default MetricCard;
